import { setNotification } from "../slice/miscSlice";
import {
  addConsignmentFiles,
  addShipment,
  editConsignment,
  editConsignmentItem,
  setASN,
  setASNList,
  setASNSelection,
  setCancelConsignments,
  setConsignment,
  setConsignmentBills,
  setConsignmentFiles,
  setConsignmentItems,
  setConsignmentItemsStatus,
  setConsignmentNotes,
  setConsignments,
  setConsignmentsSelection,
  setConsignmentsSelectionV2,
  setConsignmentStatus,
  setDeliveries,
  setDelivery,
  setDeliverySelection,
  setEquipments,
  setEquipmentsSelection,
  setEquipmentTypeCategories,
  setEquipmentTypes,
  setEquipmentTypesSelection,
  setShipment,
  setShipmentLeg,
  setShipmentLegCarriersSelection,
  setShipmentLegDeliverTosSelection,
  setShipmentLegDestinationsSelection,
  setShipmentLegOriginsSelection,
  setShipmentLegs,
  setShipmentLegSequencesSelection,
  setShipmentLegVehicleReferencesSelection,
  setShipments,
  setShipmentsV1,
  setShipmentV1,
  setShipmentV1Selection,
  updateConsignment,
  updateConsignmentForASNValidation,
  updateDeliveryStatusInList,
  updateShipment,
  updateShipmentLegStatus,
  updateShipmentLine,
  updateShipmentV1StatusInList,
  updateTrackingInfo,
} from "../slice/shipmentSlice";
import { apiSlice } from "./apiSlice";
import { dispatchAddNote, patchNoteAvailability, resolveNoteOrderType } from "./noteCache";
import { operationsApiSlice } from "./operationsApiSlice";
import { fileResponseHandler } from "./utils";

export const shipmentApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    approveShipment: builder.mutation({
      query: ({ shipmentId }) => ({
        url: `/api_shipment/shipment/${shipmentId}/approval/`,
        method: "PATCH",
        body: {},
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateShipment(result.data)))
          .catch(error => {
            setNotification({
              type: "error",
              message: error.data?.detail || "Something went wrong",
            });
          });
      },
    }),
    cancelConsignments: builder.mutation({
      query: data => ({
        url: "/api_shipment/consignment/bulk_cancel/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => dispatch(setCancelConsignments(data))).catch(error => {});
      },
    }),
    closeConsignmentPreview: builder.mutation({
      query: data => ({
        url: "/api_shipment/consignment/close_preview/",
        method: "POST",
        body: data,
      }),
    }),
    closeConsignments: builder.mutation({
      query: data => ({
        url: "/api_shipment/consignment/close/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["consignmentList", "filteredConsignmentList", "consignmentDetail"],
    }),
    reopenConsignment: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api_shipment/consignment/${id}/reopen/`,
        method: "PATCH",
        body: { reason },
      }),
      invalidatesTags: (_result, error, { id }) =>
        error
          ? []
          : [
              "consignmentList",
              "filteredConsignmentList",
              { type: "consignmentDetail", id: Number(id) },
            ],
    }),
    changeConsignment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/consignment/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, error, { id }) =>
        error
          ? []
          : [
              "consignmentList",
              "filteredConsignmentList",
              { type: "consignmentDetail", id: Number(id) },
            ],
    }),
    downloadConsignmentPdf: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_shipment/consignment/${id}/download_pdf/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    downloadConsignmentExcel: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_shipment/consignment/${id}/download_excel/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    shareConsignment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/consignment/${id}/share/`,
        method: "POST",
        body: data,
      }),
    }),
    downloadASNPdf: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_shipment/asn/${id}/download_pdf/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    downloadASNExcel: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_shipment/asn/${id}/download_excel/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    shareASN: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/asn/${id}/share/`,
        method: "POST",
        body: data,
      }),
    }),
    confirmASN: builder.mutation({
      query: ({ id }) => ({
        url: `/api_shipment/asn/${id}/confirm/`,
        method: "PATCH",
        body: {},
      }),
    }),
    cancelASN: builder.mutation({
      query: ({ id }) => ({
        url: `/api_shipment/asn/${id}/cancel/`,
        method: "PATCH",
        body: {},
      }),
    }),
    bulkASNStatus: builder.mutation({
      query: data => ({
        url: "/api_shipment/asn/bulk_status/",
        method: "PATCH",
        body: data,
      }),
    }),
    createASN: builder.mutation({
      query: data => ({ url: "/api_shipment/asn/", method: "post", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateConsignment(result.data))).catch(error => {});
      },
    }),
    getASN: builder.query({
      query: id => `/api_shipment/asn/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setASN(result.data))).catch(() => {});
      },
    }),
    createConsignmentBill: builder.mutation({
      query: data => ({
        url: `/api_invoice/bill/create_from_consignment/`,
        method: "POST",
        body: data,
      }),
    }),
    createConsignmentFiles: builder.mutation({
      query: data => ({
        url: `/api_docdigit/document/create_consignment_files/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(addConsignmentFiles(result.data)))
          .catch(error => {});
      },
    }),
    /**
     * Creates a note or task and invalidates detail/list caches across Consignments,
     * Purchase Orders, and Transfer Orders based on targetId and orderType.
     *
     * @type {Object}
     */
    createConsignmentNote: builder.mutation({
      query: ({ orderType, orderId, ...data }) => ({
        url: "/api_chat/note/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled, getState }) => {
        queryFulfilled
          .then(result => {
            const orderType = resolveNoteOrderType(data.type || data.orderType);
            const targetId = data.orderId || data.id;
            dispatchAddNote(dispatch, orderType, result.data);
            if (orderType === "shipment" && targetId) {
              patchNoteAvailability(dispatch, getState, targetId, true);
            }
            if (orderType === "shipment") {
              dispatch(
                apiSlice.util.invalidateTags(["consignmentList", "filteredConsignmentList"]),
              );
            } else if (orderType === "purchase") {
              dispatch(apiSlice.util.invalidateTags(["purchaseOrderList"]));
            } else if (orderType === "transfer") {
              dispatch(apiSlice.util.invalidateTags(["transferOrderList"]));
            }
            if (orderType === "shipment" && targetId) {
              dispatch(
                apiSlice.util.invalidateTags([{ type: "consignmentDetail", id: Number(targetId) }]),
              );
            }
            if (orderType === "purchase" && targetId) {
              dispatch(
                apiSlice.util.invalidateTags([
                  { type: "purchaseOrderDetail", id: String(targetId) },
                ]),
              );
            }
            if (orderType === "transfer" && targetId) {
              dispatch(
                apiSlice.util.invalidateTags([
                  { type: "transferOrderDetail", id: Number(targetId) },
                ]),
              );
            }
            dispatch(operationsApiSlice.util.invalidateTags(["OperationsOverview"]));
          })
          .catch(() => {});
      },
    }),
    createShipment: builder.mutation({
      query: data => ({ url: "/api_shipment/shipment/", method: "post", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(addShipment(result.data))).catch(error => {});
      },
    }),
    editShipment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/shipment/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateShipment(result.data))).catch(error => {});
      },
    }),
    editShipmentLine: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/shipment_line/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateShipmentLine(result.data))).catch(error => {});
      },
    }),
    getASNList: builder.query({
      query: params => (params ? `/api_shipment/asn/?${params}` : "/api_shipment/asn/"),
      providesTags: ["asnList"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setASNList(result.data))).catch(error => {});
      },
    }),
    getASNSelection: builder.query({
      query: () => "/api_shipment/asn/?mode=selection",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            const data = result.data;
            const payload = Array.isArray(data)
              ? data
              : Array.isArray(data?.results)
                ? data.results
                : [];
            dispatch(setASNSelection(payload));
          })
          .catch(() => {
            dispatch(setASNSelection([]));
          });
      },
    }),
    getShipmentLegCarriersSelection: builder.query({
      query: () => "/api/shipment/v1/shipment_leg/carriers/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentLegCarriersSelection(result.data)))
          .catch(() => {});
      },
    }),
    getShipmentLegOriginsSelection: builder.query({
      query: () => "/api/shipment/v1/shipment_leg/origins/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentLegOriginsSelection(result.data)))
          .catch(() => {});
      },
    }),
    getShipmentLegDestinationsSelection: builder.query({
      query: () => "/api/shipment/v1/shipment_leg/destinations/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentLegDestinationsSelection(result.data)))
          .catch(() => {});
      },
    }),
    getShipmentLegDeliverTosSelection: builder.query({
      query: () => "/api/shipment/v1/shipment_leg/deliver_tos/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentLegDeliverTosSelection(result.data)))
          .catch(() => {});
      },
    }),
    getShipmentLegVehicleReferencesSelection: builder.query({
      query: () => "/api/shipment/v1/shipment_leg/vehicle_references/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentLegVehicleReferencesSelection(result.data)))
          .catch(() => {});
      },
    }),

    getShipmentLegSequencesSelection: builder.query({
      query: () => "/api/shipment/v1/shipment_leg/sequences/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentLegSequencesSelection(result.data)))
          .catch(() => {});
      },
    }),
    getConsignment: builder.query({
      query: id => `/api_shipment/consignment/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "consignmentDetail", id: Number(id) }],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setConsignment(result.data))).catch(error => {});
      },
    }),
    getConsignmentForUpdate: builder.query({
      query: id => `/api_shipment/consignment/${id}/?mode=update`,
      providesTags: _ => ["consignmentUpdateInfo"],
    }),
    getConsignmentNotes: builder.query({
      query: params => (params ? `/api_chat/note/?${params}` : "/api_chat/note/"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentNotes(result.data)))
          .catch(error => {});
      },
    }),
    getConsignmentItems: builder.query({
      query: params =>
        params ? `/api_shipment/consignment_item/?${params}` : "/api_shipment/consignment_item/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentItems(result.data)))
          .catch(error => {});
      },
    }),
    getConsignmentItemsStatus: builder.query({
      query: () => "/api_shipment/consignment_item_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentItemsStatus(result.data)))
          .catch(error => {});
      },
    }),
    /**
     * Shipment header statuses used by the shipment list status filter.
     * Line statuses omit partial shipment states.
     */
    getConsignmentStatus: builder.query({
      query: () => "/api_shipment/consignment_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentStatus(result.data)))
          .catch(error => {});
      },
    }),
    getConsignments: builder.query({
      query: params =>
        params ? `/api_shipment/consignment/?${params}` : "/api_shipment/consignment/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setConsignments(result.data))).catch(error => {});
      },
      providesTags: _ => ["consignmentList"],
    }),
    getConsignmentsSelection: builder.query({
      query: params =>
        params
          ? `/api_shipment/consignment/?mode=selection&${params}`
          : "/api_shipment/consignment/?mode=selection",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentsSelection(result.data)))
          .catch(error => {});
      },
    }),
    getConsignmentsSelectionV2: builder.query({
      query: params =>
        params
          ? `/api_shipment/consignment/?mode=selection&${params}`
          : "/api_shipment/consignment/?mode=selection",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentsSelectionV2(result.data)))
          .catch(error => {});
      },
    }),
    getDeliverySelection: builder.query({
      query: params =>
        params
          ? `/api/shipment/v1/delivery/selection/?${params}`
          : "/api/shipment/v1/delivery/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setDeliverySelection(result.data)))
          .catch(error => {});
      },
    }),
    getDownloadedConsignmentItems: builder.query({
      query: params =>
        params
          ? `/api_shipment/consignment_item/download_records/?${params}`
          : "/api_shipment/consignment_item/download_records/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => {}).catch(error => {});
      },
    }),
    getBillsForConsignment: builder.query({
      query: params => `/api_invoice/bill/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentBills(result.data)))
          .catch(error => {});
      },
    }),
    getFilesForConsignment: builder.query({
      query: params => `/api_docdigit/document/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentFiles(result.data)))
          .catch(error => {});
      },
    }),
    getShipment: builder.query({
      query: ({ id, params = null }) =>
        params ? `/api_shipment/shipment/${id}/?${params}` : `/api_shipment/shipment/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShipment(result.data))).catch(error => {});
      },
    }),
    getShipments: builder.query({
      query: (params = null) => `/api_shipment/shipment/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShipments(result.data))).catch(error => {});
      },
    }),
    getDeliveryList: builder.query({
      query: params =>
        params ? `/api/shipment/v1/delivery/?${params}` : "/api/shipment/v1/delivery/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setDeliveries(result.data))).catch(error => {});
      },
      providesTags: _ => ["deliveryList"],
    }),
    getDeliveryDetail: builder.query({
      query: id => `/api/shipment/v1/delivery/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setDelivery(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "deliveryDetail", id }],
    }),
    getDeliveryLines: builder.query({
      query: params => ({
        url: "/api/shipment/v1/delivery_line/",
        params,
      }),
    }),
    createDelivery: builder.mutation({
      query: data => ({
        url: "/api/shipment/v1/delivery/",
        method: "POST",
        body: data,
      }),
    }),
    patchDelivery: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/shipment/v1/delivery/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateDeliveryStatus: builder.mutation({
      query: ({ id, status_id }) => ({
        url: `/api/shipment/v1/delivery/${id}/update_status/`,
        method: "PATCH",
        body: { status_id },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateDeliveryStatusInList(data));
        } catch (error) {}
      },
    }),
    updateShipmentStatus: builder.mutation({
      query: ({ id, status_id }) => ({
        url: `/api/shipment/v1/shipment/${id}/update_status/`,
        method: "PATCH",
        body: { status_id },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateShipmentV1StatusInList(data));
        } catch (error) {}
      },
    }),
    getShipmentV1List: builder.query({
      query: params =>
        params ? `/api/shipment/v1/shipment/?${params}` : "/api/shipment/v1/shipment/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShipmentsV1(result.data))).catch(error => {});
      },
      providesTags: _ => ["shipmentV1List"],
    }),
    getShipmentV1Detail: builder.query({
      query: id => `/api/shipment/v1/shipment/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShipmentV1(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "shipmentV1Detail", id }],
    }),
    reverseShipmentDispatch: builder.mutation({
      query: ({ id, note }) => ({
        url: `/api/shipment/v1/shipment/${id}/reverse-dispatch/`,
        method: "POST",
        body: { note: note || "" },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "shipmentV1Detail", id },
        "shipmentV1List",
      ],
    }),
    getShipmentV1Lines: builder.query({
      query: params => ({
        url: "/api/shipment/v1/shipment_line/",
        params,
      }),
    }),
    getFreightForwarders: builder.query({
      query: (params = null) =>
        params
          ? `/api/shipment/v1/shipment/freight_forwarders/?${params}`
          : `/api/shipment/v1/shipment/freight_forwarders/`,
    }),
    getEquipmentCategories: builder.query({
      query: (params = null) =>
        params
          ? `/api/shipment/v1/equipment_type/categories/?${params}`
          : `/api/shipment/v1/equipment_type/categories/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setEquipmentTypeCategories(result.data)))
          .catch(error => {});
      },
    }),
    getEquipmentTypes: builder.query({
      query: (params = null) =>
        params ? `/api/shipment/v1/equipment_type/?${params}` : `/api/shipment/v1/equipment_type/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setEquipmentTypes(result.data))).catch(error => {});
      },
      providesTags: _ => ["equipmentTypeList"],
    }),
    getEquipmentTypesSelection: builder.query({
      query: (params = null) => {
        const queryStr = typeof params === "string" ? params : params?.params;
        return queryStr
          ? `/api/shipment/v1/equipment_type/selection/?${queryStr}`
          : "/api/shipment/v1/equipment_type/selection/";
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setEquipmentTypesSelection(result.data)))
          .catch(error => {});
      },
    }),
    getEquipmentTypeById: builder.query({
      query: id => `/api/shipment/v1/equipment_type/${id}/`,
      providesTags: (result, error, id) => [{ type: "equipmentTypeDetail", id }],
    }),
    createEquipmentType: builder.mutation({
      query: data => ({
        url: "/api/shipment/v1/equipment_type/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["equipmentTypeList"],
    }),
    updateEquipmentType: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/shipment/v1/equipment_type/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "equipmentTypeList",
        { type: "equipmentTypeDetail", id },
      ],
    }),
    getEquipments: builder.query({
      query: (params = null) =>
        params ? `/api/shipment/v1/equipment/?${params}` : `/api/shipment/v1/equipment/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setEquipments(result.data))).catch(error => {});
      },
      providesTags: _ => ["equipmentList"],
    }),
    getEquipmentsSelection: builder.query({
      query: (params = null) => {
        const queryStr = typeof params === "string" ? params : params?.params;
        return queryStr
          ? `/api/shipment/v1/equipment/selection/?${queryStr}`
          : "/api/shipment/v1/equipment/selection/";
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setEquipmentsSelection(result.data)))
          .catch(error => {});
      },
    }),
    getEquipmentById: builder.query({
      query: id => `/api/shipment/v1/equipment/${id}/`,
      providesTags: (result, error, id) => [{ type: "equipmentDetail", id }],
    }),
    createEquipment: builder.mutation({
      query: data => ({
        url: "/api/shipment/v1/equipment/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["equipmentList"],
    }),
    updateEquipment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/shipment/v1/equipment/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "equipmentList",
        { type: "equipmentDetail", id },
      ],
    }),
    exportEquipments: builder.mutation({
      query: params => ({
        url: `/api/shipment/v1/equipment/export_excel/${params ? `?${params}` : ""}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    createShipmentV1: builder.mutation({
      query: data => ({
        url: "/api/shipment/v1/shipment/",
        method: "POST",
        body: data,
      }),
    }),
    patchShipmentV1: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/shipment/v1/shipment/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    syncASN: builder.mutation({
      query: () => ({ url: "/api_shipment/sync_asn/", method: "post", body: {} }),
    }),
    updateConsignmentItemETA: builder.mutation({
      query: data => ({
        url: `/api_shipment/consignment_item/update_eta/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(editConsignmentItem(result.data)))
          .catch(error => {});
      },
    }),
    updateConsignmentDefaultUOMPack: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/consignment/${id}/update_default_pack_uom/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(editConsignment(result.data))).catch(error => {});
      },
    }),
    updateConsignmentReference: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_shipment/consignment/${id}/update_reference/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(editConsignment(result.data))).catch(error => {});
      },
    }),
    updateInternationalConsignment: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_shipment/consignment/${id}/update_consignment_shipment/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(editConsignment(result.data.data)))
          .catch(error => {});
      },
    }),
    updateTrackingInfo: builder.mutation({
      query: data => ({ url: "/api_shipment/mutate_container/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateTrackingInfo(result.data))).catch(error => {});
      },
    }),
    validateConsignmentForASN: builder.mutation({
      query: ({ id }) => ({
        url: `/api_shipment/consignment/${id}/validate_for_asn/`,
        method: "PATCH",
        body: {},
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateConsignmentForASNValidation(result.data)))
          .catch(error => {});
      },
    }),
    getShipmentLegList: builder.query({
      query: params =>
        params ? `/api/shipment/v1/shipment_leg/?${params}` : "/api/shipment/v1/shipment_leg/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShipmentLegs(result.data))).catch(error => {});
      },
      providesTags: _ => ["shipmentLegList"],
    }),
    getShipmentLegDetail: builder.query({
      query: id => `/api/shipment/v1/shipment_leg/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShipmentLeg(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "shipmentLegDetail", id }],
    }),
    getShipmentLegLines: builder.query({
      query: params => ({
        url: "/api/shipment/v1/shipment_leg_line/",
        params,
      }),
    }),
    getShipmentSelection: builder.query({
      query: params =>
        params
          ? `/api/shipment/v1/shipment/selection/?${params}`
          : "/api/shipment/v1/shipment/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShipmentV1Selection(result.data)))
          .catch(error => {});
      },
    }),
    getShipmentLineSelection: builder.query({
      query: params =>
        params
          ? `/api/shipment/v1/shipment_line/selection/?${params}`
          : "/api/shipment/v1/shipment_line/selection/",
    }),
    getShipmentLegSelection: builder.query({
      query: params =>
        params
          ? `/api/shipment/v1/shipment_leg/selection/?${params}`
          : "/api/shipment/v1/shipment_leg/selection/",
    }),
    createShipmentLegLine: builder.mutation({
      query: data => ({
        url: "/api/shipment/v1/shipment_leg_line/",
        method: "POST",
        body: data,
      }),
    }),
    deleteShipmentLegLine: builder.mutation({
      query: id => ({
        url: `/api/shipment/v1/shipment_leg_line/${id}/`,
        method: "DELETE",
      }),
    }),
    getShipmentLegCarriers: builder.query({
      query: (params = null) =>
        params
          ? `/api/shipment/v1/shipment_leg/carriers/?${params}`
          : `/api/shipment/v1/shipment_leg/carriers/`,
    }),
    createShipmentLeg: builder.mutation({
      query: data => ({
        url: "/api/shipment/v1/shipment_leg/",
        method: "POST",
        body: data,
      }),
    }),
    updateShipmentLeg: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/shipment/v1/shipment_leg/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateShipmentLegStatus: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/shipment/v1/shipment_leg/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateShipmentLegStatus(result.data)))
          .catch(error => {});
      },
    }),
  }),
});

export const {
  useApproveShipmentMutation,
  useCancelConsignmentsMutation,
  useCloseConsignmentPreviewMutation,
  useCloseConsignmentsMutation,
  useReopenConsignmentMutation,
  useChangeConsignmentMutation,
  useCancelASNMutation,
  useBulkASNStatusMutation,
  useConfirmASNMutation,
  useCreateASNMutation,
  useGetASNQuery,
  useLazyDownloadConsignmentPdfQuery,
  useLazyDownloadConsignmentExcelQuery,
  useShareConsignmentMutation,
  useLazyDownloadASNPdfQuery,
  useLazyDownloadASNExcelQuery,
  useShareASNMutation,
  useCreateConsignmentBillMutation,
  useCreateConsignmentFilesMutation,
  useCreateConsignmentNoteMutation,
  useCreateShipmentMutation,
  useEditShipmentLineMutation,
  useEditShipmentMutation,
  useGetASNListQuery,
  useLazyGetASNSelectionQuery,
  useGetBillsForConsignmentQuery,
  useGetConsignmentForUpdateQuery,
  useGetConsignmentItemsQuery,
  useGetConsignmentItemsStatusQuery,
  useLazyGetConsignmentItemsStatusQuery,
  useGetConsignmentStatusQuery,
  useLazyGetConsignmentStatusQuery,
  useGetConsignmentNotesQuery,
  useGetConsignmentQuery,
  useGetConsignmentsQuery,
  useGetConsignmentsSelectionQuery,
  useGetConsignmentsSelectionV2Query,
  useLazyGetConsignmentsSelectionV2Query,
  useGetFilesForConsignmentQuery,
  useGetShipmentQuery,
  useGetShipmentsQuery,
  useLazyGetDownloadedConsignmentItemsQuery,
  useLazyGetShipmentLegCarriersSelectionQuery,
  useLazyGetShipmentLegOriginsSelectionQuery,
  useLazyGetShipmentLegDestinationsSelectionQuery,
  useLazyGetShipmentLegDeliverTosSelectionQuery,
  useLazyGetShipmentLegVehicleReferencesSelectionQuery,
  useLazyGetShipmentLegSequencesSelectionQuery,
  useSyncASNMutation,
  useUpdateConsignmentDefaultUOMPackMutation,
  useUpdateConsignmentItemETAMutation,
  useUpdateConsignmentReferenceMutation,
  useUpdateInternationalConsignmentMutation,
  useUpdateTrackingInfoMutation,
  useValidateConsignmentForASNMutation,
  useGetDeliveryListQuery,
  useLazyGetDeliveryListQuery,
  useGetDeliverySelectionQuery,
  useLazyGetDeliverySelectionQuery,
  useGetDeliveryDetailQuery,
  useGetDeliveryLinesQuery,
  useLazyGetDeliveryLinesQuery,
  useCreateDeliveryMutation,
  usePatchDeliveryMutation,
  useUpdateDeliveryStatusMutation,
  useGetShipmentV1ListQuery,
  useLazyGetShipmentV1ListQuery,
  useGetShipmentV1DetailQuery,
  useReverseShipmentDispatchMutation,
  useGetShipmentV1LinesQuery,
  useLazyGetShipmentV1LinesQuery,
  useCreateShipmentV1Mutation,
  usePatchShipmentV1Mutation,
  useGetFreightForwardersQuery,
  useLazyGetFreightForwardersQuery,
  useUpdateShipmentStatusMutation,
  useGetEquipmentCategoriesQuery,
  useLazyGetEquipmentCategoriesQuery,
  useGetEquipmentTypesQuery,
  useLazyGetEquipmentTypesQuery,
  useGetEquipmentTypesSelectionQuery,
  useLazyGetEquipmentTypesSelectionQuery,
  useGetEquipmentTypeByIdQuery,
  useCreateEquipmentTypeMutation,
  useUpdateEquipmentTypeMutation,
  useGetEquipmentsQuery,
  useLazyGetEquipmentsQuery,
  useGetEquipmentsSelectionQuery,
  useLazyGetEquipmentsSelectionQuery,
  useGetEquipmentByIdQuery,
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
  useExportEquipmentsMutation,
  useGetShipmentLegListQuery,
  useLazyGetShipmentLegListQuery,
  useGetShipmentLegDetailQuery,
  useGetShipmentLegLinesQuery,
  useLazyGetShipmentLegLinesQuery,
  useGetShipmentLegCarriersQuery,
  useUpdateShipmentLegStatusMutation,
  useLazyGetShipmentLegCarriersQuery,
  useCreateShipmentLegMutation,
  useUpdateShipmentLegMutation,
  useGetShipmentSelectionQuery,
  useLazyGetShipmentSelectionQuery,
  useGetShipmentLineSelectionQuery,
  useLazyGetShipmentLineSelectionQuery,
  useGetShipmentLegSelectionQuery,
  useLazyGetShipmentLegSelectionQuery,
  useCreateShipmentLegLineMutation,
  useDeleteShipmentLegLineMutation,
} = shipmentApiSlice;
